import { isRef, onMounted, onUnmounted, ref, Ref, watch } from "vue";
import { AutoscrollOrientationEnum } from "../enums";

interface AutoscrollOptions {
  sensitivityArea: Ref<number> | number; // Область чувствительности у границ
  maxSpeed: Ref<number> | number; // Максимальная скорость
  minSpeed: Ref<number> | number; // Минимальная скорость
  acceleration: Ref<number> | number; // Ускорение за пределами контейнера
  interval: Ref<number> | number;
  direction: Ref<AutoscrollOrientationEnum> | AutoscrollOrientationEnum; // Ориентация автопрокрутки
  emit: (event: "update:y" | "update:x", value: number) => void;
}

enum AutoscrollDirection {
  Top = "top",
  Bottom = "bottom",
  Left = "left",
  Right = "right",
}

export const useAutoscroll = (
  scrollElement: Ref<HTMLElement | null>,
  containerElement: Ref<HTMLElement | null>,
  options: AutoscrollOptions
) => {
  const autoscrollInterval = ref<NodeJS.Timeout | null>(null);
  /** Направление автопрокрутки */
  const autoscrollDirections = ref<Array<AutoscrollDirection>>([]);
  /** Текущая вертикальная скорость прокрутки */
  const currentSpeedY = ref(0);
  /** Текущая горизонтальная скорость прокрутки */
  const currentSpeedX = ref(0);
  /** Активна ли автопрокрутка */
  const isAutoscrolling = ref(false);
  /** Это сенсорное устройство */
  const isTouchDevice = ref("ontouchstart" in window);

  /** Получить значение из ref или обычного значения */
  const getValue = <T>(value: Ref<T> | T): T => {
    return isRef(value) ? value.value : value;
  };

  /** Получить размеры контейнера */
  const getContainerDimensions = () => {
    if (!containerElement.value) return null;
    const rect = containerElement.value.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      right: rect.right,
    };
  };

  /** Преобразовать процент чувствительности в пиксели */
  const getSensitivityInPixels = (dimension: "width" | "height") => {
    const dimensions = getContainerDimensions();
    if (!dimensions) return 0;
    return dimensions[dimension] * getValue(options.sensitivityArea) / 100;
  };

  /** Вычислить скорость прокрутки */
  const calculateSpeed = (
    distance: number,
    isOutside: boolean,
    sensitivityArea: number
  ): number => {
    const minSpeed = getValue(options.minSpeed);
    const maxSpeed = getValue(options.maxSpeed);
    const acceleration = getValue(options.acceleration);

    if (isOutside) {
      // За пределами контейнера - линейное увеличение скорости с ускорением
      return minSpeed + distance * acceleration;
    } else {
      // Внутри области чувствительности - квадратичная зависимость
      const normalizedDistance = Math.min(distance / sensitivityArea, 1);
      return (
        minSpeed + (maxSpeed - minSpeed) * (1 - Math.pow(normalizedDistance, 2))
      );
    }
  };

  /** Функция обработки автопрокрутки, вызывается в setInterval */
  const proccessAutoscroll = () => {
    if (!scrollElement.value) return;

    const currentScrollY = scrollElement.value.scrollTop;
    const maxScrollY =
      scrollElement.value.scrollHeight - scrollElement.value.clientHeight;
    const currentScrollX = scrollElement.value.scrollLeft;
    const maxScrollX =
      scrollElement.value.scrollWidth - scrollElement.value.clientWidth;

    let shouldScrollY = false;
    let shouldScrollX = false;
    let directionY = 0;
    let directionX = 0;

    const orientation = getValue(options.direction);

    // Вертикальная прокрутка
    if (
      orientation === AutoscrollOrientationEnum.Vertical ||
      orientation === AutoscrollOrientationEnum.Both
    ) {
      if (
        autoscrollDirections.value.includes(AutoscrollDirection.Bottom) &&
        currentScrollY < maxScrollY
      ) {
        shouldScrollY = true;
        directionY = 1;
      } else if (
        autoscrollDirections.value.includes(AutoscrollDirection.Top) &&
        currentScrollY > 0
      ) {
        shouldScrollY = true;
        directionY = -1;
      }
    }

    // Горизонтальная прокрутка
    if (
      orientation === AutoscrollOrientationEnum.Horizontal ||
      orientation === AutoscrollOrientationEnum.Both
    ) {
      if (
        autoscrollDirections.value.includes(AutoscrollDirection.Right) &&
        currentScrollX < maxScrollX
      ) {
        shouldScrollX = true;
        directionX = 1;
      } else if (
        autoscrollDirections.value.includes(AutoscrollDirection.Left) &&
        currentScrollX > 0
      ) {
        shouldScrollX = true;
        directionX = -1;
      }
    }

    if (shouldScrollY || shouldScrollX) {
      if (shouldScrollY) {
        scrollElement.value.scrollTop += directionY * currentSpeedY.value;
        options.emit("update:y", scrollElement.value.scrollTop);
      }
      if (shouldScrollX) {
        scrollElement.value.scrollLeft += directionX * currentSpeedX.value;
        options.emit("update:x", scrollElement.value.scrollLeft);
      }
    } else {
      stopAutoscroll();
    }
  };

  /** Начать автопрокрутку */
  const startAutoscroll = () => {
    if (autoscrollInterval.value || !scrollElement.value) return;

    const intervalTime = getValue(options.interval);

    isAutoscrolling.value = true;
    autoscrollInterval.value = setInterval(proccessAutoscroll, intervalTime);
  };

  /** Остановить автопрокрутку */
  const stopAutoscroll = () => {
    if (autoscrollInterval.value) {
      clearInterval(autoscrollInterval.value);
      autoscrollInterval.value = null;
    }
    autoscrollDirections.value = undefined;
    currentSpeedY.value = 0;
    currentSpeedX.value = 0;
    isAutoscrolling.value = false;
  };

  /**
   * Рассчитать вертикальную скорость прокрутки
   * @param clientY Y-координата указателя
   * @param containerTop Верхняя граница контейнера
   * @param containerBottom Нижняя граница контейнера
   */
  const calculateVerticalSpeed = (
    clientY: number,
    containerTop: number,
    containerBottom: number
  ): { speed: number; direction?: AutoscrollDirection } => {
    const sensitivityArea = getSensitivityInPixels("height");

    if (clientY < containerTop) {
      // Выше контейнера
      return {
        speed: calculateSpeed(containerTop - clientY, true, sensitivityArea),
        direction: AutoscrollDirection.Top,
      };
    } else if (clientY < containerTop + sensitivityArea) {
      // Верхняя область чувствительности
      return {
        speed: calculateSpeed(clientY - containerTop, false, sensitivityArea),
        direction: AutoscrollDirection.Top,
      };
    } else if (clientY > containerBottom) {
      // Ниже контейнера
      return {
        speed: calculateSpeed(clientY - containerBottom, true, sensitivityArea),
        direction: AutoscrollDirection.Bottom,
      };
    } else if (clientY > containerBottom - sensitivityArea) {
      // Нижняя область чувствительности
      return {
        speed: calculateSpeed(
          containerBottom - clientY,
          false,
          sensitivityArea
        ),
        direction: AutoscrollDirection.Bottom,
      };
    }

    return { speed: 0 };
  };

  /**
   * Рассчитать горизонтальную скорость прокрутки
   * @param clientX X-координата указателя
   * @param containerLeft Левая граница контейнера
   * @param containerRight Правая граница контейнера
   */
  const calculateHorizontalSpeed = (
    clientX: number,
    containerLeft: number,
    containerRight: number
  ): { speed: number; direction?: AutoscrollDirection } => {
    const sensitivityArea = getSensitivityInPixels("width");

    if (clientX < containerLeft) {
      // Слева от контейнера
      return {
        speed: calculateSpeed(containerLeft - clientX, true, sensitivityArea),
        direction: AutoscrollDirection.Left,
      };
    } else if (clientX < containerLeft + sensitivityArea) {
      // Левая область чувствительности
      return {
        speed: calculateSpeed(clientX - containerLeft, false, sensitivityArea),
        direction: AutoscrollDirection.Left,
      };
    } else if (clientX > containerRight) {
      // Справа от контейнера
      return {
        speed: calculateSpeed(clientX - containerRight, true, sensitivityArea),
        direction: AutoscrollDirection.Right,
      };
    } else if (clientX > containerRight - sensitivityArea) {
      // Правая область чувствительности
      return {
        speed: calculateSpeed(containerRight - clientX, false, sensitivityArea),
        direction: AutoscrollDirection.Right,
      };
    }

    return { speed: 0 };
  };

  /** Обновить направление и скорость прокрутки на основе позиции указателя */
  const updateScrollDirection = (clientX: number, clientY: number) => {
    const dimensions = getContainerDimensions();
    if (!dimensions) return;

    const orientation = getValue(options.direction);
    const newDirections = new Array<AutoscrollDirection>();
    let newVerticalSpeed = 0;
    let newHorizontalSpeed = 0;

    // Рассчет вертикальной скорости и направления
    if (
      orientation === AutoscrollOrientationEnum.Vertical ||
      orientation === AutoscrollOrientationEnum.Both
    ) {
      const { speed, direction } = calculateVerticalSpeed(
        clientY,
        dimensions.top,
        dimensions.bottom
      );
      newVerticalSpeed = speed;
      if (direction) newDirections.push(direction);
    }

    // Рассчет горизонтальной скорости и направления
    if (
      orientation === AutoscrollOrientationEnum.Horizontal ||
      orientation === AutoscrollOrientationEnum.Both
    ) {
      const { speed, direction } = calculateHorizontalSpeed(
        clientX,
        dimensions.left,
        dimensions.right
      );
      newHorizontalSpeed = speed;
      if (direction) newDirections.push(direction);
    }

    currentSpeedY.value = newVerticalSpeed;
    currentSpeedX.value = newHorizontalSpeed;

    autoscrollDirections.value = newDirections;
    newDirections.length !== 0 ? startAutoscroll() : stopAutoscroll();
  };

  /** Обработчик движения указателя */
  const handlePointerMove = (e: MouseEvent | TouchEvent) => {
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    updateScrollDirection(clientX, clientY);
  };

  /** Обработчик отпускания указателя */
  const handlePointerUp = () => {
    stopAutoscroll();
  };

  /** Обработчик нажатия указателя */
  const handlePointerDown = (e: MouseEvent | TouchEvent) => {
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    updateScrollDirection(clientX, clientY);
  };

  const initEvents = () => {
    // Удаляем предыдущие обработчики событий
    window.removeEventListener("mousemove", handlePointerMove);
    window.removeEventListener("touchmove", handlePointerMove);
    window.removeEventListener("mouseup", handlePointerUp);
    window.removeEventListener("touchend", handlePointerUp);
    window.removeEventListener("mousedown", handlePointerDown);
    window.removeEventListener("touchstart", handlePointerDown);

    if (isTouchDevice.value) {
      window.addEventListener("touchmove", handlePointerMove, {
        passive: false,
      });
      window.addEventListener("touchend", handlePointerUp);
      window.addEventListener("touchstart", handlePointerDown);
    } else {
      window.addEventListener("mousemove", handlePointerMove);
      window.addEventListener("mouseup", handlePointerUp);
      window.addEventListener("mousedown", handlePointerDown);
    }
  };

  watch(
    () => getValue(options.direction),
    (orientation) => {
      if (orientation === undefined) stopAutoscroll();
      else initEvents();
    },
    { immediate: true }
  );

  onMounted(() => {
    if (getValue(options.direction) !== undefined) initEvents();
  });

  onUnmounted(() => {
    stopAutoscroll();
    window.removeEventListener("mousemove", handlePointerMove);
    window.removeEventListener("touchmove", handlePointerMove);
    window.removeEventListener("mouseup", handlePointerUp);
    window.removeEventListener("touchend", handlePointerUp);
    window.removeEventListener("mousedown", handlePointerDown);
    window.removeEventListener("touchstart", handlePointerDown);
  });
};
